import type { ClientDto } from "@iagenix/shared";
import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { queries } from "@/lib/queries";

export const TODOS_CLIENTES = "__todos__";

/** Seletor "Todos os clientes / cliente X" usado nas telas de BMs, contas, páginas e pixels. */
export function ClientFilter({
  workspaceId,
  value,
  onChange,
  className,
}: {
  workspaceId: string;
  value: string | undefined;
  onChange: (clientId: string | undefined) => void;
  className?: string;
}) {
  const clients = useQuery(queries.clients(workspaceId));
  const list = clients.data ?? [];
  if (!clients.isPending && list.length === 0) return null;
  return (
    <Select
      value={value ?? TODOS_CLIENTES}
      onValueChange={(v) => onChange(v === TODOS_CLIENTES ? undefined : v)}
    >
      <SelectTrigger className={className ?? "sm:w-56"} aria-label="Filtrar por cliente">
        <SelectValue placeholder="Cliente" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TODOS_CLIENTES}>Todos os clientes</SelectItem>
        {list.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            <ClientDot client={c} /> {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ClientDot({ client }: { client: Pick<ClientDto, "color"> }) {
  return (
    <span
      aria-hidden
      className="inline-block size-2.5 shrink-0 rounded-full align-middle"
      style={{ backgroundColor: client.color }}
    />
  );
}

/** Etiqueta "● Nome do cliente" ou "—" para uma linha de ativo. */
export function ClientTag({
  clientId,
  clients,
}: {
  clientId: string | null;
  clients: ClientDto[] | undefined;
}) {
  const client = clientId ? clients?.find((c) => c.id === clientId) : undefined;
  if (!client) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <ClientDot client={client} />
      {client.name}
    </span>
  );
}
